'use client';

import { useEffect, useState } from 'react';

import { Pause, Play } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { HeroBlock, HeroSlide } from '~/(tenant)/_lib/schema/landing-config.schema';
import { HeroSlideView } from './hero-slide';
import type { LandingRenderContext } from './landing-context';

const ROTATE_MS = 6000;

export function HeroCarousel({
  slides,
  ctx,
  align,
  heightClass,
  options,
}: {
  slides: HeroSlide[];
  ctx: LandingRenderContext;
  align: 'start' | 'center';
  heightClass: string;
  options?: HeroBlock;
}) {
  const [state, setState] = useState({ active: 0, paused: false, hovered: false, reducedMotion: false });
  const isArabic = ctx.locale.startsWith('ar');
  const paused = state.paused || state.hovered || state.reducedMotion;
  const active = state.active % slides.length;

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setState((current) => ({ ...current, reducedMotion: media.matches }));
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const timer = setInterval(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      setState((current) => ({ ...current, active: (current.active + 1) % slides.length }));
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [slides.length, paused]);

  return (
    <div
      role="region"
      aria-roledescription={isArabic ? 'عرض شرائح' : 'carousel'}
      aria-label={isArabic ? 'أبرز المحتوى' : 'Highlights'}
      className={cn('relative grid w-full overflow-hidden', heightClass)}
      onMouseEnter={() => setState((current) => ({ ...current, hovered: true }))}
      onMouseLeave={() => setState((current) => ({ ...current, hovered: false }))}
      onFocusCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setState((current) => ({ ...current, paused: true }));
        }
      }}
    >
      <div className="grid" aria-live={paused ? 'polite' : 'off'}>
        {slides.map((slide, i) => (
          <div
            key={i}
            role="group"
            aria-roledescription={isArabic ? 'شريحة' : 'slide'}
            aria-label={`${i + 1} / ${slides.length}`}
            aria-hidden={i !== active}
            inert={i !== active}
            className={cn(
              'col-start-1 row-start-1 grid transition-opacity duration-700 motion-reduce:transition-none',
              i === active ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <HeroSlideView slide={slide} ctx={ctx} align={align} heightClass={heightClass} options={options} />
          </div>
        ))}
      </div>

      <div className={cn('absolute bottom-3 z-20 flex items-center text-white', options?.controls === 'minimal' ? 'end-4' : 'start-1/2 -translate-x-1/2 rounded-full bg-black/70 px-2 rtl:translate-x-1/2')}>
        {!state.reducedMotion ? (
          <button
            type="button"
            onClick={() => setState((current) => ({ ...current, paused: !current.paused }))}
            className="min-h-11 px-3 text-xs font-medium"
            aria-label={state.paused ? (isArabic ? 'تشغيل عرض الشرائح' : 'Play slideshow') : (isArabic ? 'إيقاف عرض الشرائح' : 'Pause slideshow')}
          >
            {options?.controls === 'minimal' ? (state.paused ? <Play size={12} aria-hidden /> : <Pause size={12} aria-hidden />) : state.paused ? (isArabic ? 'تشغيل' : 'Play') : (isArabic ? 'إيقاف' : 'Pause')}
          </button>
        ) : null}
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${isArabic ? 'الشريحة' : 'Slide'} ${i + 1}`}
            aria-current={i === active ? 'true' : undefined}
            onClick={() => setState((current) => ({ ...current, active: i, paused: true }))}
            className="flex h-11 w-11 items-center justify-center"
          >
            <span className={cn('h-1.5 rounded-full', i === active ? options?.controls === 'minimal' ? 'w-6 bg-primary' : 'w-6 bg-white' : 'w-1.5 bg-white/50')} />
          </button>
        ))}
      </div>
    </div>
  );
}
